<template>
  <Modal
    :mask-closable="false"
    v-model="modalVisible"
    width="535px"
    :title="langMap.trackAttackTitle[lang]"
    class="behavior-prohibition-modal"
  >
    <div class="behavior-prohibition-container">

      <!-- 图片展示区域 -->
      <div class="image-display-section" v-if="data.img">
        <div class="image-container">
          <img 
            :src="data.img" 
            class="track-image"
            alt="告警图片"
          />
        </div>
      </div>

      <!-- 目标列表区域 -->
      <div class="target-list-section" v-if="data.targetList && data.targetList.length > 0">
        <div class="target-list">
          <div 
            v-for="(target, index) in data.targetList"
            :key="index"
            class="target-item"
            :class="{ 'selected': selectedTarget === index }"
            @click="selectTarget(index)"
          >
            <div class="target-number">{{ index + 1 }}</div>
            <div class="target-info">
              <div class="target-label">{{ langMap.target[lang] }} {{ index + 1 }}</div>
              <div class="target-description">
                <span class="target-detail">{{ langMap.targetId[lang] }}: {{ target.targetId }}</span>
              </div>
            </div>
            <div class="target-status" v-if="selectedTarget === index">
              <Icon type="md-checkmark-circle" color="#52c41a" size="20" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div slot="footer">
      <div class="modal-footer">
        <Button 
          type="default" 
          @click="cancel"
          class="cancel-btn"
        >
          {{ langMap.cancel[lang] }}
        </Button>
        <Button 
          type="primary" 
          @click="confirm"
          :disabled="selectedTarget === null"
          class="confirm-btn"
          :loading="loading"
        >
          <Icon type="md-send" />
          {{ langMap.confirmTrackAttack[lang] }}
        </Button>
      </div>
    </div>
  </Modal>
</template>

<script>
import api from '@/api/index.js';
import langMap from '@/utils/language';
import { mapState } from 'vuex';

export default {
  name: 'BehaviorProhibitionModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    data: {
      type: Object,
      default: () => ({
        img: '',
        x:'',
        y:'',
        z:'',
        targetList: []
      })
    },
    robotCode: {
      type: String,
      required: true
    },
    timestamp: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      langMap: langMap,
      modalVisible: false,
      selectedTarget: null,
      loading: false
    };
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
    })
  },
  watch: {
    visible(newVal) {
      this.modalVisible = newVal;
      if (newVal) {
        this.selectedTarget = null;
      }
    },
    modalVisible(newVal) {
      if (!newVal) {
        this.$emit('update:visible', false);
      }
    }
  },
  methods: {
    // 选择目标
    selectTarget(index) {
      this.selectedTarget = index;
    },

    // 取消操作
    cancel() {
      this.modalVisible = false;
      this.selectedTarget = null;
      this.$emit('cancel');
    },

    // 确认行为拒止
    confirm() {
      if (this.selectedTarget === null) {
        this.$Message.warning(langMap.pleaseSelectTarget[this.lang]);
        return;
      }

      this.$Modal.confirm({
        title: langMap.systemTip[this.lang],
        content: langMap.confirmTrackAttack[this.lang] + ` (${langMap.target[this.lang]} ${this.selectedTarget + 1})`,
        onOk: () => {
          this.executeBehaviorProhibition();
        }
      });
    },

    // 执行行为拒止
    executeBehaviorProhibition() {
      this.loading = true;
      
      const selectedTargetData = this.data.targetList[this.selectedTarget];
      const trackData = {
        timestamp: this.timestamp,
        ret: 3, // 行为拒止操作类型
        targetPursuitInfo: {
          targetId: selectedTargetData.targetId,
          x:this.data.x,
          y:this.data.y,
          z:this.data.z,
        }
      };

      api
        .postData(
          "notice-server/cmd/sentiment",
          { code: this.robotCode, data: trackData },
          this
        )
        .then(res => {
          this.loading = false;
          if (res.state == 0) {
            this.$Notice.error({
              title: langMap.tip[this.lang],
              desc: res.msg
            });
            this.$emit('error', res);
          } else {
            this.$Notice.success({
              title: langMap.success[this.lang],
              desc: res.message
            });
            this.modalVisible = false;
            this.selectedTarget = null;
            this.$emit('success', res);
          }
        })
        .catch(error => {
          this.loading = false;
          this.$emit('error', error);
        });
    }
  }
};
</script>

<style lang="less" scoped>
/* 行为拒止弹窗样式 - 科幻风格 */
.behavior-prohibition-container {
  padding: 0;
  max-height: 80vh;
  overflow-y: auto;
  background-color: #030813;
  position: relative;
  
  /* 添加背景网格效果 */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      linear-gradient(rgba(0, 30, 60, 0.05) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0, 30, 60, 0.05) 1px, transparent 1px);
    background-size: 20px 20px;
    z-index: 0;
    pointer-events: none;
  }
}

.task-info-section {
  padding: 12px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  color: white;
  
  .task-info-content {
    padding: 0;
  }
  
  .task-message {
    font-size: 13px;
    line-height: 1.4;
    margin: 0;
    background: rgba(255, 255, 255, 0.1);
    padding: 8px 12px;
    border-radius: 6px;
    backdrop-filter: blur(10px);
  }
}

.image-display-section {
  margin-bottom: 16px;
  position: relative;
  z-index: 1;
  
  .image-container {
    border-radius: 4px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
    border: 1px solid rgba(32, 194, 255, 0.3);
    transition: all 0.3s ease;
    position: relative;
    background: rgba(13, 25, 40, 0.6);
    
    /* 添加角落装饰 */
    &::before,
    &::after {
      content: '';
      position: absolute;
      width: 12px;
      height: 12px;
      border: 2px solid #20c2ff;
      transition: all 0.3s ease;
      z-index: 2;
    }
    
    &::before {
      top: 0;
      left: 0;
      border-right: none;
      border-bottom: none;
    }
    
    &::after {
      bottom: 0;
      right: 0;
      border-left: none;
      border-top: none;
    }
    
    &:hover {
      border-color: rgba(32, 194, 255, 0.6);
      box-shadow: 0 6px 30px rgba(32, 194, 255, 0.3);
      
      &::before,
      &::after {
        width: 16px;
        height: 16px;
        box-shadow: 0 0 10px rgba(32, 194, 255, 0.8);
      }
    }
    
    .track-image {
      width: 100%;
      height: auto;
      display: block;
      max-height: 500px;
      object-fit: contain;
      position: relative;
      z-index: 1;
    }
  }
}

.target-list-section {
  margin-bottom: 12px;
  
  .target-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 200px;
    overflow-y: auto;
    padding: 4px;
  }
}

.target-item {
  display: flex;
  align-items: center;
  padding: 12px 15px;
  border: 1px solid rgba(32, 194, 255, 0.3);
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, rgba(13, 25, 40, 0.8), rgba(13, 25, 40, 0.5));
  position: relative;
  margin-bottom: 8px;
  
  /* 添加左侧装饰条 */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: linear-gradient(to bottom, transparent, #20c2ff, transparent);
    transition: all 0.3s ease;
  }
  
  &:hover {
    border-color: rgba(32, 194, 255, 0.5);
    box-shadow: 0 4px 15px rgba(32, 194, 255, 0.2);
    transform: translateX(3px);
    
    &::before {
      box-shadow: 0 0 10px rgba(32, 194, 255, 0.6);
    }
  }
  
  &.selected {
    border-color: rgba(0, 255, 136, 0.6);
    background: linear-gradient(135deg, rgba(0, 255, 136, 0.1), rgba(13, 25, 40, 0.8));
    box-shadow: 0 6px 20px rgba(0, 255, 136, 0.3);
    transform: translateX(3px);
    
    &::before {
      background: linear-gradient(to bottom, transparent, #00ff88, transparent);
      box-shadow: 0 0 15px rgba(0, 255, 136, 0.8);
    }
  }
  
  .target-number {
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, rgba(231, 76, 60, 0.8), rgba(192, 57, 43, 0.8));
    color: #fff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 14px;
    margin-right: 15px;
    flex-shrink: 0;
    border: 2px solid rgba(32, 194, 255, 0.4);
    box-shadow: 0 0 15px rgba(231, 76, 60, 0.5);
    transition: all 0.3s ease;
  }
  
  &.selected .target-number {
    background: linear-gradient(135deg, rgba(0, 255, 136, 0.8), rgba(0, 204, 106, 0.8));
    border-color: rgba(0, 255, 136, 0.6);
    box-shadow: 0 0 20px rgba(0, 255, 136, 0.8);
    animation: pulse-target 2s infinite;
  }
  
  .target-info {
    flex: 1;
    
    .target-label {
      font-weight: 600;
      color: #20c2ff;
      margin-bottom: 6px;
      font-size: 15px;
      text-shadow: 0 0 8px rgba(32, 194, 255, 0.3);
    }
    
    .target-description {
      color: rgba(255, 255, 255, 0.7);
      margin-bottom: 4px;
      font-size: 12px;
      line-height: 1.4;
      
      .target-detail {
        display: inline-block;
        margin-right: 12px;
        font-size: 12px;
        color: rgba(255, 255, 255, 0.6);
      }
    }
  }
  
  .target-status {
    margin-left: 12px;
    
    .ivu-icon {
      filter: drop-shadow(0 0 5px rgba(0, 255, 136, 0.8));
    }
  }
}

@keyframes pulse-target {
  0%, 100% {
    box-shadow: 0 0 20px rgba(0, 255, 136, 0.8);
  }
  50% {
    box-shadow: 0 0 30px rgba(0, 255, 136, 1);
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 8px;
  
  .cancel-btn {
    min-width: 100px;
    height: 40px;
    border-radius: 4px;
    font-weight: 500;
    background: rgba(13, 25, 40, 0.8);
    border: 1px solid rgba(32, 194, 255, 0.3);
    color: rgba(255, 255, 255, 0.9);
    transition: all 0.3s ease;
    
    &:hover {
      border-color: rgba(32, 194, 255, 0.6);
      box-shadow: 0 0 15px rgba(32, 194, 255, 0.3);
      background: rgba(13, 25, 40, 1);
    }
  }
  
  .confirm-btn {
    min-width: 140px;
    height: 40px;
    border-radius: 4px;
    font-weight: 500;
    background: linear-gradient(135deg, rgba(0, 255, 136, 0.2), rgba(32, 194, 255, 0.2));
    border: 1px solid rgba(0, 255, 136, 0.5);
    color: #fff;
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;
    
    /* 添加流光效果 */
    &::before {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
      transition: all 0.6s ease;
    }
    
    &:hover:not(:disabled) {
      border-color: rgba(0, 255, 136, 0.8);
      box-shadow: 0 0 25px rgba(0, 255, 136, 0.5);
      background: linear-gradient(135deg, rgba(0, 255, 136, 0.3), rgba(32, 194, 255, 0.3));
      
      &::before {
        transform: translate(50%, 50%);
      }
    }
    
    &:disabled {
      background: rgba(13, 25, 40, 0.5);
      border-color: rgba(255, 255, 255, 0.1);
      color: rgba(255, 255, 255, 0.3);
      box-shadow: none;
    }
  }
}

@keyframes glow-line {
  0% {
    opacity: 0.5;
    box-shadow: 0 0 5px rgba(32, 194, 255, 0.3);
  }
  100% {
    opacity: 1;
    box-shadow: 0 0 15px rgba(32, 194, 255, 0.6);
  }
}

/* 滚动条样式 - 科幻风格 */
.target-list::-webkit-scrollbar,
.behavior-prohibition-container::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}

.target-list::-webkit-scrollbar-track,
.behavior-prohibition-container::-webkit-scrollbar-track {
  background: rgba(13, 25, 40, 0.6);
  border-radius: 2px;
}

.target-list::-webkit-scrollbar-thumb,
.behavior-prohibition-container::-webkit-scrollbar-thumb {
  background: rgba(32, 194, 255, 0.4);
  border-radius: 2px;
  transition: all 0.3s ease;
}

.target-list::-webkit-scrollbar-thumb:hover,
.behavior-prohibition-container::-webkit-scrollbar-thumb:hover {
  background: rgba(32, 194, 255, 0.6);
  box-shadow: 0 0 10px rgba(32, 194, 255, 0.5);
}
</style>

<!-- 全局样式覆盖，确保弹窗背景色正确 -->
<style lang="less">
/* 弹窗样式覆盖 - 科幻风格 */
.behavior-prohibition-modal {
  .ivu-modal {
    border-radius: 4px;
    overflow: hidden;
    background: #030813 !important;
    border: 1px solid rgba(32, 194, 255, 0.3);
    box-shadow: 0 10px 50px rgba(0, 0, 0, 0.5);
  }
  
  .ivu-modal-content {
    background: #030813 !important;
    box-shadow: none;
  }
  
  .ivu-modal-header {
    background: linear-gradient(135deg, rgba(13, 25, 40, 0.95), rgba(13, 25, 40, 0.85));
    color: #20c2ff;
    padding: 20px 24px;
    border-bottom: 1px solid rgba(32, 194, 255, 0.3);
    position: relative;
    
    /* 添加标题装饰 */
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 2px;
      background: linear-gradient(90deg, transparent, #20c2ff, transparent);
      animation: glow-line 2s infinite alternate;
    }
    
    .ivu-modal-header-inner {
      font-size: 18px;
      font-weight: 600;
      letter-spacing: 2px;
      text-shadow: 0 0 10px rgba(32, 194, 255, 0.5);
      color:#fff
    }
    
    .ivu-modal-close {
      color: rgba(255, 255, 255, 0.7);
      transition: all 0.3s ease;
      
      &:hover {
        color: #20c2ff;
        text-shadow: 0 0 10px rgba(32, 194, 255, 0.8);
      }
    }
  }
  
  .ivu-modal-body {
    padding: 20px !important;
    background: #030813 !important;
    color: rgba(255, 255, 255, 0.9);
  }
  
  .ivu-modal-footer {
    padding: 16px 24px 24px;
    border-top: 1px solid rgba(32, 194, 255, 0.2);
    background: linear-gradient(to bottom, rgba(13, 25, 40, 0.5), rgba(13, 25, 40, 0.8));
  }
}
.behavior-prohibition-modal {
  .ivu-modal {
    background-color: #030813 !important;
  }
  
  .ivu-modal-content {
    background-color: #030813 !important;
    border-radius: 4px !important;
  }
  
  .ivu-modal-body {
    background-color: #030813 !important;
  }
  
  .ivu-modal-header {
    background: linear-gradient(135deg, rgba(13, 25, 40, 0.95), rgba(13, 25, 40, 0.85)) !important;
    border-bottom: 1px solid rgba(32, 194, 255, 0.3) !important;
  }
  
  .ivu-modal-footer {
    background: linear-gradient(to bottom, rgba(13, 25, 40, 0.5), rgba(13, 25, 40, 0.8)) !important;
    border-top: 1px solid rgba(32, 194, 255, 0.2) !important;
  }
}
</style>
